import { CommonModule } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ChartModule } from 'primeng/chart';
import { DatePickerModule } from 'primeng/datepicker';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { finalize } from 'rxjs';
import { Subscription } from 'rxjs';
import { Company } from '../../company/company.model';
import { TenantScope } from '../../company/tenant-access.model';
import { TenantScopeService } from '../../company/tenant-scope.service';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';
import { DashboardCompanyScopeSelectorComponent } from '../../shared/page-layout/dashboard-company-scope-selector.component';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageSearchPanelComponent } from '../../shared/page-layout/page-search-panel.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { ContainerTrackingService } from '../container-tracking.service';
import { ContainerTerminalYardService } from '../container-terminal-yard.service';
import { ContainerTrackingDashboardOverview } from '../models/container-tracking-dashboard-overview.model';
import { ContainerTrackingDashboardBoardItem } from '../models/container-tracking-dashboard-row.model';
import { ContainerYardOccupancyOverview, ContainerYardOccupancyRow } from '../models/container-terminal-yard.model';

interface DashboardFilters {
  fromDate: Date | null;
  toDate: Date | null;
  status: string | null;
  currentState: string;
  containerNumber: string;
  billOfLadingNumber: string;
  customer: string;
  yardId: number | null;
  facilityType: string | null;
  handoffStatus: string | null;
  exceptionSeverity: string | null;
  readinessStatus: string | null;
}

@Component({
  selector: 'app-container-tracking-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BreadcrumbModule,
    ButtonModule,
    ChartModule,
    DatePickerModule,
    IftaLabelModule,
    InputGroupAddonModule,
    InputGroupModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    DashboardCompanyScopeSelectorComponent,
    PageDataPanelComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageSearchPanelComponent,
    PageShellComponent
  ],
  templateUrl: './container-tracking-dashboard.component.html',
  styleUrls: ['./container-tracking-dashboard.component.css']
})
export class ContainerTrackingDashboardComponent implements OnInit, OnDestroy {
  canViewDashboard = false;
  canViewCases = false;
  canCreateCase = false;
  isLoading = false;
  companyId: number | null = null;
  companyGroupId: number | null = null;
  activeGroupName = '';
  groupCompanies: Company[] = [];
  selectedGroupCompanyId: number | null = null;
  private readonly subscriptions = new Subscription();

  breadcrumbItems: MenuItem[] = [
    { label: 'Container Tracking' },
    { label: 'Overview' }
  ];
  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

  filters: DashboardFilters = this.defaultFilters();

  statusOptions = [
    { label: 'Open', value: 'OPEN' },
    { label: 'In Progress', value: 'IN_PROGRESS' },
    { label: 'On Hold', value: 'ON_HOLD' },
    { label: 'Completed', value: 'COMPLETED' },
    { label: 'Cancelled', value: 'CANCELLED' }
  ];
  readinessOptions = [
    { label: 'Available', value: 'AVAILABLE' },
    { label: 'Blocked', value: 'BLOCKED' },
    { label: 'Pending', value: 'PENDING' },
    { label: 'Return Complete', value: 'RETURN_COMPLETE' }
  ];
  severityOptions = [
    { label: 'Danger', value: 'danger' },
    { label: 'Warning', value: 'warn' },
    { label: 'Info', value: 'info' }
  ];
  handoffStatusOptions = [
    { label: 'Planned', value: 'PLANNED' },
    { label: 'Ready', value: 'READY' },
    { label: 'In Progress', value: 'IN_PROGRESS' },
    { label: 'Completed', value: 'COMPLETED' },
    { label: 'Blocked', value: 'BLOCKED' },
    { label: 'Exception', value: 'EXCEPTION' }
  ];
  facilityTypeOptions = [
    { label: 'Port Terminal', value: 'PORT_TERMINAL' },
    { label: 'Bonded Yard', value: 'BONDED_YARD' },
    { label: 'CFS / Transit Shed', value: 'CFS' },
    { label: 'Warehouse', value: 'WAREHOUSE' },
    { label: 'Customer Site', value: 'CUSTOMER_SITE' },
    { label: 'Empty Depot', value: 'EMPTY_DEPOT' },
    { label: 'Carrier Yard', value: 'CARRIER_YARD' },
    { label: 'Third Party Yard', value: 'THIRD_PARTY_YARD' }
  ];

  overview: ContainerTrackingDashboardOverview = {};
  yardOccupancy: ContainerYardOccupancyOverview = {};
  statusChartData: any;
  exceptionChartData: any;
  agingChartData: any;
  chartOptions = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { boxWidth: 10, usePointStyle: true }
      }
    }
  };

  constructor(
    private containerTrackingService: ContainerTrackingService,
    private terminalYardService: ContainerTerminalYardService,
    private permissionService: PermissionService,
    private tenantScopeService: TenantScopeService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.canViewDashboard = this.permissionService.hasPermission(app_permissions.container_tracking.dashboard.view, 'ANY');
    this.canViewCases = this.permissionService.hasPermission(app_permissions.container_tracking.cases.view);
    this.canCreateCase = this.permissionService.hasPermission(app_permissions.container_tracking.cases.create);
    this.subscriptions.add(this.tenantScopeService.activeScope$.subscribe(scope => this.handleScopeChange(scope)));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get metrics(): PageMetric[] {
    const summary = this.overview.summary || {};
    return [
      { label: 'Open Cases', value: summary.openCases || 0, tone: 'info' },
      { label: 'In Progress', value: summary.inProgressCases || 0, tone: 'warn' },
      { label: 'On Hold', value: summary.onHoldCases || 0, tone: 'danger' },
      { label: 'Active Transport', value: summary.activeTransportLegs || 0, tone: 'info' },
      { label: 'Active Storage', value: summary.activeStorageLegs || 0, tone: 'warn' },
      { label: 'Active Yard Visits', value: summary.activeYardVisits || 0, tone: 'info' },
      { label: 'Overdue Transport', value: summary.overdueTransport || 0, tone: 'danger' },
      { label: 'Blocked Readiness', value: summary.blockedReadiness || 0, tone: 'danger' },
      { label: 'Blocked Handoffs', value: summary.blockedHandoffs || 0, tone: (summary.blockedHandoffs || 0) > 0 ? 'danger' : 'success' },
      { label: 'Empty Return Pending', value: summary.emptyReturnPending || 0, tone: 'warn' },
      { label: 'Exceptions', value: summary.activeExceptions || 0, tone: 'danger' }
    ];
  }

  get activeFilterCount(): number {
    return Object.values(this.filters).filter(value => value !== null && value !== '').length;
  }

  get showGroupCompanySelector(): boolean {
    return !!this.companyGroupId && this.groupCompanies.length > 0;
  }

  loadDashboard(): void {
    if (!this.companyId && !this.companyGroupId) {
      return;
    }
    this.isLoading = true;
    this.containerTrackingService.getDashboardOverview(this.buildParams())
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: overview => {
          this.overview = overview || {};
          this.buildCharts();
          this.loadYardOccupancy();
        },
        error: () => {
          this.overview = {};
          this.yardOccupancy = {};
          this.buildCharts();
        }
      });
  }

  clearFilters(): void {
    this.filters = this.defaultFilters();
    this.loadDashboard();
  }

  createCase(): void {
    this.router.navigate(['/container-tracking/cases/add']);
  }

  viewCases(): void {
    this.router.navigate(['/container-tracking/cases']);
  }

  openCase(caseId?: number): void {
    if (caseId) {
      this.router.navigate(['/container-tracking/cases/view', caseId]);
    }
  }

  openYardInventory(row?: ContainerYardOccupancyRow): void {
    const queryParams: Record<string, number> = {};
    if (row?.facilityId) {
      queryParams['facilityId'] = row.facilityId;
    }
    if (row?.areaId) {
      queryParams['areaId'] = row.areaId;
    }
    this.router.navigate(['/container-tracking/inventory'], { queryParams });
  }

  onDashboardCompanyScopeChange(companyId: number | null): void {
    this.selectedGroupCompanyId = companyId;
    this.loadDashboard();
  }

  tagSeverity(value?: string): string {
    switch ((value || '').toLowerCase()) {
      case 'danger':
      case 'blocked':
      case 'on_hold':
      case 'overdue':
        return 'danger';
      case 'warn':
      case 'warning':
      case 'in_progress':
      case 'pending':
        return 'warn';
      case 'success':
      case 'available':
      case 'completed':
      case 'ready':
      case 'clear':
      case 'none':
      case 'return_complete':
        return 'success';
      case 'info':
      case 'open':
        return 'info';
      default:
        return 'secondary';
    }
  }

  sourceModuleLabel(sourceModule?: string): string {
    switch ((sourceModule || '').toUpperCase()) {
      case 'WAREHOUSING_YARD': return 'Terminal Yard';
      case 'CONTAINER_HANDOFF_ORCHESTRATION':
      case 'HANDOFF': return 'Handoff';
      case 'WAREHOUSING': return 'Warehouse';
      case 'CLEARANCE': return 'Clearance';
      case 'SHIPMENT': return 'Shipment';
      case 'CONTAINER_TRACKING': return 'Container Tracking';
      case 'SYSTEM': return 'System';
      default:
        return sourceModule ? sourceModule.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase()) : '-';
    }
  }

  private buildParams(): HttpParams {
    let params = new HttpParams();
    if (this.companyGroupId) {
      params = params.set('companyGroupId', String(this.companyGroupId));
      if (this.selectedGroupCompanyId) {
        params = params.set('memberCompanyId', String(this.selectedGroupCompanyId));
      }
    } else if (this.companyId) {
      params = params.set('companyId', String(this.companyId));
    }
    Object.entries(this.filters).forEach(([key, value]) => {
      if (value === null || value === '') {
        return;
      }
      if (value instanceof Date) {
        params = params.set(key, this.formatDate(value));
      } else {
        params = params.set(key, String(value));
      }
    });
    return params;
  }

  private buildCharts(): void {
    this.statusChartData = this.toChartData(this.overview.statusBreakdown || [], [
      '#0ea5e9', '#f59e0b', '#ef4444', '#10b981', '#64748b'
    ]);
    this.exceptionChartData = this.toExceptionChartData();
    this.agingChartData = this.toChartData(this.overview.agingBuckets || [], [
      '#10b981', '#0ea5e9', '#f59e0b', '#ef4444'
    ]);
  }

  private loadYardOccupancy(): void {
    const companyId = this.selectedGroupCompanyId || this.companyId;
    if (!companyId) {
      this.yardOccupancy = {};
      return;
    }
    this.terminalYardService.occupancy(new HttpParams().set('companyId', String(companyId))).subscribe({
      next: overview => this.yardOccupancy = overview || {},
      error: () => this.yardOccupancy = {}
    });
  }

  private toChartData(items: ContainerTrackingDashboardBoardItem[], colors: string[]) {
    return {
      labels: items.map(item => item.label || item.key || '-'),
      datasets: [{
        data: items.map(item => item.count || 0),
        backgroundColor: colors
      }]
    };
  }

  private toExceptionChartData() {
    const counts = new Map<string, number>();
    (this.overview.exceptionQueue || []).forEach(row => {
      const label = this.sourceModuleLabel(row.sourceModule || 'SYSTEM');
      counts.set(label, (counts.get(label) || 0) + 1);
    });
    return {
      labels: Array.from(counts.keys()),
      datasets: [{
        data: Array.from(counts.values()),
        backgroundColor: ['#ef4444', '#f59e0b', '#0ea5e9', '#14b8a6', '#64748b']
      }]
    };
  }

  private defaultFilters(): DashboardFilters {
    return {
      fromDate: null,
      toDate: null,
      status: null,
      currentState: '',
      containerNumber: '',
      billOfLadingNumber: '',
      customer: '',
      yardId: null,
      facilityType: null,
      handoffStatus: null,
      exceptionSeverity: null,
      readinessStatus: null
    };
  }

  private formatDate(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private handleScopeChange(scope: TenantScope | null): void {
    const nextCompanyId = scope?.type === 'COMPANY' ? scope.companyId : null;
    const nextCompanyGroupId = scope?.type === 'GROUP' ? scope.companyGroupId : null;
    this.activeGroupName = scope?.type === 'GROUP' ? scope.companyGroupName : '';
    this.groupCompanies = scope?.type === 'GROUP' ? (scope.memberCompanies ?? []) : [];
    if (!this.groupCompanies.some(company => company.id === this.selectedGroupCompanyId)) {
      this.selectedGroupCompanyId = null;
    }
    if (this.companyId === nextCompanyId && this.companyGroupId === nextCompanyGroupId) {
      return;
    }

    this.companyId = nextCompanyId;
    this.companyGroupId = nextCompanyGroupId;
    this.selectedGroupCompanyId = null;
    if (!this.canViewDashboard || (!this.companyId && !this.companyGroupId)) {
      this.overview = {};
      this.buildCharts();
      return;
    }
    this.loadDashboard();
  }
}
